import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';

import Modal from '../../../components/modal';
import Input from '../../../components/input';
import Button from '../../../components/button';
import SearchableSelect from '../../../components/searchableSelect';
import Spinner from '../../../components/spinner';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import {
  listIpsecSiteConnections,
  listVpnServices,
  listIkePolicies,
  listIpsecPolicies,
  listVpnEndpointGroups,
} from '../../../hooks/local/userReducer';
import { showSuccessMessage, setFormikErrors } from '../../../hooks/constants';

const enumOptions = {
  initiator: ['bi-directional', 'response-only'].map((v) => ({
    value: v,
    label: v,
  })),
  dpd_action: ['hold', 'clear', 'restart', 'disabled'].map((v) => ({
    value: v,
    label: v,
  })),
};

const VpnSiteConnections = () => {
  document.title = 'IPSec Site Connections - VPN';

  const projectId = useSelector((state) => state.user.selectedProject?.id);
  const dispatch = useDispatch();
  const {
    siteConnections: list,
    vpnServices: services,
    ikePolicies,
    ipsecPolicies,
    endpointGroups,
  } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: '', name: '' });

  const refetch = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await Promise.all([
        dispatch(listIpsecSiteConnections(projectId)),
        dispatch(listVpnServices(projectId)),
        dispatch(listIkePolicies(projectId)),
        dispatch(listIpsecPolicies(projectId)),
        dispatch(listVpnEndpointGroups(projectId)),
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(list) ? list : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [list, searchText]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filtered.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filtered.length / entriesPerPage) || 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const toOptions = (arr, placeholder) => {
    const list = Array.isArray(arr) ? arr : [];
    return [
      { value: '', label: placeholder },
      ...list.map((x) => ({
        value: x.id || x.uuid || x.name,
        label: x.name || x.id || x.uuid || placeholder,
      })),
    ];
  };

  const serviceOptions = useMemo(
    () => toOptions(services, 'Select VPN service'),
    [services],
  );
  const ikeOptions = useMemo(
    () => toOptions(ikePolicies, 'Select IKE policy'),
    [ikePolicies],
  );
  const ipsecOptions = useMemo(
    () => toOptions(ipsecPolicies, 'Select IPSec policy'),
    [ipsecPolicies],
  );
  const endpointGroupOptions = useMemo(
    () => toOptions(endpointGroups, 'Select endpoint group'),
    [endpointGroups],
  );

  const createForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      vpnservice_id: '',
      ikepolicy_id: '',
      ipsecpolicy_id: '',
      peer_address: '',
      peer_id: '',
      psk: '',
      mtu: 1500,
      initiator: 'bi-directional',
      admin_state_up: true,
      // DPD
      action: 'restart',
      interval: 30,
      timeout: 120,
      // routing
      local_ep_group_id: '',
      peer_ep_group_id: '',
      local_id: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      vpnservice_id: Yup.string().required('VPN Service is required'),
      ikepolicy_id: Yup.string().required('IKE Policy is required'),
      ipsecpolicy_id: Yup.string().required('IPSec Policy is required'),
      peer_address: Yup.string().required('Peer address is required'),
      peer_id: Yup.string().required('Peer ID is required'),
      psk: Yup.string()
        .min(8, 'PSK should be at least 8 characters')
        .required('PSK is required'),
      mtu: Yup.number().required('MTU is required'),
      timeout: Yup.number().required('DPD timeout is required'),
      local_ep_group_id: Yup.string().required('Local EP Group is required'),
      peer_ep_group_id: Yup.string().required('Peer EP Group is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;

      const payload = {
        name: values.name,
        description: values.description,
        vpnservice_id: values.vpnservice_id,
        ikepolicy_id: values.ikepolicy_id,
        ipsecpolicy_id: values.ipsecpolicy_id,
        peer_address: values.peer_address,
        peer_id: values.peer_id,
        psk: values.psk,
        mtu: Number(values.mtu),
        initiator: values.initiator,
        admin_state_up: !!values.admin_state_up,
        action: values.action,
        interval: Number(values.interval),
        timeout: Number(values.timeout),
        local_ep_group_id: values.local_ep_group_id,
        peer_ep_group_id: values.peer_ep_group_id,
      };

      if (values.local_id?.trim()) payload.local_id = values.local_id.trim();

      setLoading(true);
      try {
       await apiEndPoints.createIpsecSiteConnection(projectId,
        { ipsec_site_connection: payload,});
        showSuccessMessage('IPSec Site Connection created');
        setCreateOpen(false);
        resetForm();
        await refetch();
      } catch (error) {
        setFormikErrors(error, createForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selected?.name || '',
      description: selected?.description || '',
      vpnservice_id: selected?.vpnservice_id || '',
      ikepolicy_id: selected?.ikepolicy_id || '',
      ipsecpolicy_id: selected?.ipsecpolicy_id || '',
      peer_address: selected?.peer_address || '',
      peer_id: selected?.peer_id || '',
      psk: '',
      mtu: selected?.mtu ?? 1500,
      initiator: selected?.initiator || 'bi-directional',
      admin_state_up: selected?.admin_state_up ?? true,
      action: selected?.action || 'restart',
      interval: selected?.interval ?? 30,
      timeout: selected?.timeout ?? 120,
      local_ep_group_id: selected?.local_ep_group_id || '',
      peer_ep_group_id: selected?.peer_ep_group_id || '',
      local_id: selected?.local_id || '',
    },
    validationSchema: Yup.object({
      peer_address: Yup.string().required('Peer address is required'),
      peer_id: Yup.string().required('Peer ID is required'),
      timeout: Yup.number().required('DPD timeout is required'),
      action: Yup.string().required('DPD action is required'),
      local_ep_group_id: Yup.string().required('Local EP Group is required'),
      peer_ep_group_id: Yup.string().required('Peer EP Group is required'),
    }),
      onSubmit: async (values) => {
      if (!projectId || !selected?.id) return;

      const payload = {
        description: values.description,
        admin_state_up: !!values.admin_state_up,
        initiator: values.initiator,
        interval: Number(values.interval),
        timeout: Number(values.timeout),
        action: values.action,
        peer_address: values.peer_address,
        peer_id: values.peer_id,
        local_ep_group_id: values.local_ep_group_id,
        peer_ep_group_id: values.peer_ep_group_id,
      };

      if (values.local_id?.trim()) {
        payload.local_id = values.local_id.trim();
      }

      if (values.psk?.trim()) {
        payload.psk = values.psk;
      }

      setLoading(true);

      try {
        await apiEndPoints.updateIpsecSiteConnection(
          projectId,
          selected.id || selected.uuid,
          {
            ipsec_site_connection: payload,
          }
        );

        showSuccessMessage(
          'IPSec Site Connection updated'
        );

        setEditOpen(false);
        setSelected(null);

        await refetch();
      } catch (error) {
        setFormikErrors(
          error,
          editForm.setErrors
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const runDelete = async () => {
    if (!projectId || !confirmDelete.id) return;
    setLoading(true);
    try {
      await apiEndPoints.deleteIpsecSiteConnection(projectId, confirmDelete.id);
      showSuccessMessage('IPSec Site Connection deleted');
      setConfirmDelete({ open: false, id: '', name: '' });
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const downloadConfig = async (siteConnectionId) => {
    if (!projectId || !siteConnectionId) return;
    setLoading(true);
    try {
      const res = await apiEndPoints.downloadVpnConfig(projectId, siteConnectionId);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vpn-config-${siteConnectionId}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccessMessage('VPN config downloaded');
    } finally {
      setLoading(false);
    }
  };

  const renderRouting = (form) => {
    return (
      <div className="grid gap-3 border rounded-md border-[#c4c4c432] p-3">
        <p className="text-sm font-semibold text-primaryBlue">Routing</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchableSelect
            label="Local Endpoint Group"
            name="local_ep_group_id"
            value={form.values.local_ep_group_id}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            options={endpointGroupOptions}
            onError={form.touched.local_ep_group_id ? form.errors.local_ep_group_id : null}
            isLabelIcon={true}
            iconTitle="Local subnets which the new IPsec connection is connected to. Required if no subnet is specified in a VPN service selected."
          />
          <SearchableSelect
            label="Peer Endpoint Group"
            name="peer_ep_group_id"
            value={form.values.peer_ep_group_id}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            options={endpointGroupOptions}
            onError={form.touched.peer_ep_group_id ? form.errors.peer_ep_group_id : null}
            isLabelIcon={true}
            iconTitle="Remote subnets which the new IPsec connection is connected to. Required if no peer CIDR is specified."
          />
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen">
      <Spinner loading={loading} />

      <p className="crimson-font text-xl font-medium text-primaryBlue mb-6">
        IPSec Site Connections
      </p>

      <div className="grid md:flex md:items-center md:justify-between gap-4 mb-4">
        <div className="w-full md:w-1/2 lg:w-1/3">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search site connection..."
            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
          />
        </div>
        <div
          onClick={() => setCreateOpen(true)}
          className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
        >
          Create site connection
        </div>
      </div>

      <div className="overflow-x-auto rounded-md dark:text-white">
        <table className="w-full text-sm text-left overflow-hidden">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th className="px-6 py-4 whitespace-nowrap">Name</th>
              <th className="px-6 py-4 whitespace-nowrap">VPN Service</th>
              <th className="px-6 py-4 whitespace-nowrap">IKE Policy</th>
              <th className="px-6 py-4 whitespace-nowrap">IPSec Policy</th>
              <th className="px-6 py-4 whitespace-nowrap">Peer Address</th>
              <th className="px-6 py-4 whitespace-nowrap">Peer CIDRs</th>
              <th className="px-6 py-4 whitespace-nowrap">Status</th>
              <th className="px-6 py-4 whitespace-nowrap w-[360px]"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {(Array.isArray(currentEntries) ? currentEntries : []).map((row) => (
              <tr
                key={row.id || row.uuid || row.name}
                className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
              >
                <td className="text-start py-4 px-6 text-primaryBlue">{row.name || '—'}</td>
                <td className="text-start py-4 px-6">{row.vpnservice_id || '—'}</td>
                <td className="text-start py-4 px-6">{row.ikepolicy_id || '—'}</td>
                <td className="text-start py-4 px-6">{row.ipsecpolicy_id || '—'}</td>
                <td className="text-start py-4 px-6">{row.peer_address || '—'}</td>
                <td className="text-start py-4 px-6">
                  {Array.isArray(row.peer_cidrs) ? row.peer_cidrs.join(', ') : '—'}
                </td>
                <td className="text-start py-4 px-6">{row.status || '—'}</td>
                <td className="text-start py-4 px-6 flex items-center gap-2">
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer"
                    onClick={() => downloadConfig(row.id || row.uuid)}
                  >
                    Download config
                  </button>
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer"
                    onClick={() => {
                      setSelected(row);
                      setEditOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() =>
                      setConfirmDelete({
                        open: true,
                        id: row.id || row.uuid,
                        name: row.name || row.id || row.uuid,
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!filtered || filtered.length === 0) && (
              <tr>
                <td className="px-6 py-6 text-sm opacity-70" colSpan={8}>
                  No site connections found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === 1 ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm text-primaryBlue dark:text-white">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === totalPages ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <Modal
        title="Create IPSec Site Connection"
        isVisible={createOpen}
        onClose={() => setCreateOpen(false)}
        size="md:w-[85%] md:max-h-[95%]"
      >
        <form className="grid gap-4" onSubmit={createForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={createForm.values.name}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.name ? createForm.errors.name : null}
          />
          <Input
            label="Description"
            name="description"
            value={createForm.values.description}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.description ? createForm.errors.description : null}
          />

          <SearchableSelect
            label="VPN Service *"
            name="vpnservice_id"
            value={createForm.values.vpnservice_id}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            options={serviceOptions}
            onError={
              createForm.touched.vpnservice_id ? createForm.errors.vpnservice_id : null
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="IKE Policy *"
              name="ikepolicy_id"
              value={createForm.values.ikepolicy_id}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              options={ikeOptions}
              onError={createForm.touched.ikepolicy_id ? createForm.errors.ikepolicy_id : null}
            />
            <SearchableSelect
              label="IPSec Policy *"
              name="ipsecpolicy_id"
              value={createForm.values.ipsecpolicy_id}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              options={ipsecOptions}
              onError={createForm.touched.ipsecpolicy_id ? createForm.errors.ipsecpolicy_id : null}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Peer Address *"
              name="peer_address"
              value={createForm.values.peer_address}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              onError={
                createForm.touched.peer_address ? createForm.errors.peer_address : null
              }
            />
            <Input
              label="Peer ID *"
              name="peer_id"
              value={createForm.values.peer_id}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              onError={createForm.touched.peer_id ? createForm.errors.peer_id : null}
            />
          </div>

          <Input
            label="Pre-Shared Key (PSK) *"
            name="psk"
            type="password"
            isPassword="true"
            value={createForm.values.psk}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.psk ? createForm.errors.psk : null}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="MTU"
              name="mtu"
              type="number"
              value={createForm.values.mtu}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              onError={createForm.touched.mtu ? createForm.errors.mtu : null}
            />
            <SearchableSelect
              label="Initiator"
              name="initiator"
              value={createForm.values.initiator}
              onChange={createForm.handleChange}
              onBlur={createForm.handleBlur}
              options={enumOptions.initiator}
              onError={createForm.touched.initiator ? createForm.errors.initiator : null}
            />
          </div>

          {renderRouting(createForm)}

          <div className="grid gap-3 border rounded-md border-[#c4c4c432] p-3">
            <p className="text-sm font-semibold text-primaryBlue">Dead Peer Detection (DPD)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SearchableSelect
                label="Action"
                name="action"
                value={createForm.values.action}
                onChange={createForm.handleChange}
                onBlur={createForm.handleBlur}
                options={enumOptions.dpd_action}
                onError={createForm.touched.action ? createForm.errors.action : null}
              />
              <Input
                label="Interval (s)"
                name="interval"
                type="number"
                value={createForm.values.interval}
                onChange={createForm.handleChange}
                onBlur={createForm.handleBlur}
                onError={createForm.touched.interval ? createForm.errors.interval : null}
                isLabelIcon={true}
                iconTitle="Valid integer lesser than DPD timeout."
              />
              <Input
                label="Timeout (s)"
                name="timeout"
                type="number"
                value={createForm.values.timeout}
                onChange={createForm.handleChange}
                onBlur={createForm.handleBlur}
                onError={createForm.touched.timeout ? createForm.errors.timeout : null}
                isLabelIcon={true}
                iconTitle="Valid integer greater than the DPD interval."
              />
            </div>
          </div>

          <Input
            label="Local ID (optional)"
            name="local_id"
            value={createForm.values.local_id}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.local_id ? createForm.errors.local_id : null}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="admin_state_up"
              checked={!!createForm.values.admin_state_up}
              onChange={createForm.handleChange}
            />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill className="text-sm text-primaryBlue ml-1 cursor-pointer" title="The state of IPsec site connection to start in. If disabled (not checked), IPsec site connection does not forward packets." />
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <Modal
        title="Edit IPSec Site Connection"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        size="md:w-[85%] md:max-h-[95%]"
      >
        <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
          <Input
            label="Name *"
            name="name"
            value={editForm.values.name}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.name ? editForm.errors.name : null}
          />
          <Input
            label="Description"
            name="description"
            value={editForm.values.description}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.description ? editForm.errors.description : null}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Peer Address *"
              name="peer_address"
              value={editForm.values.peer_address}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.peer_address ? editForm.errors.peer_address : null}
              isLabelIcon={true}
              iconTitle="Peer gateway public IPv4/IPv6 Address or FQDN."
            />
            <Input
              label="Peer ID *"
              name="peer_id"
              value={editForm.values.peer_id}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.peer_id ? editForm.errors.peer_id : null}
              isLabelIcon={true}
              iconTitle="Peer router identity for authentication (Peer ID)."
            />
          </div>

          <Input
            label="PSK (leave empty to keep existing)"
            name="psk"
            type="password"
            isPassword="true"
            value={editForm.values.psk}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.psk ? editForm.errors.psk : null}
            isLabelIcon={true}
            iconTitle="Pre-Shared Key (PSK) string."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="MTU"
              name="mtu"
              type="number"
              value={editForm.values.mtu}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.mtu ? editForm.errors.mtu : null}
              isLabelIcon={true}
              iconTitle="Equal to or greater than 68 if the local subnet is IPv4. Equal to or greater than 1280 if the local subnet is IPv6."
            />
            <SearchableSelect
              label="Initiator"
              name="initiator"
              value={editForm.values.initiator}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions.initiator}
              onError={editForm.touched.initiator ? editForm.errors.initiator : null}
            />
          </div>

          {renderRouting(editForm)}

          <div className="grid gap-3 border rounded-md border-[#c4c4c432] p-3">
            <p className="text-sm font-semibold text-primaryBlue">Dead Peer Detection (DPD)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SearchableSelect
                label="Action"
                name="action"
                value={editForm.values.action}
                onChange={editForm.handleChange}
                onBlur={editForm.handleBlur}
                options={enumOptions.dpd_action}
                onError={editForm.touched.action ? editForm.errors.action : null}
              />
              <Input
                label="Interval (s)"
                name="interval"
                type="number"
                value={editForm.values.interval}
                onChange={editForm.handleChange}
                onBlur={editForm.handleBlur}
                onError={editForm.touched.interval ? editForm.errors.interval : null}
                isLabelIcon={true}
                iconTitle="Valid integer lesser than DPD timeout."
              />
              <Input
                label="Timeout (s)"
                name="timeout"
                type="number"
                value={editForm.values.timeout}
                onChange={editForm.handleChange}
                onBlur={editForm.handleBlur}
                onError={editForm.touched.timeout ? editForm.errors.timeout : null}
                isLabelIcon={true}
                iconTitle="Valid integer greater than the DPD interval."
              />
            </div>
          </div>

          <Input
            label="Local ID (optional)"
            name="local_id"
            value={editForm.values.local_id}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="admin_state_up"
              checked={!!editForm.values.admin_state_up}
              onChange={editForm.handleChange}
            />
            <span className="text-sm flex items-center">
              Admin State Up
              <BsFillPatchQuestionFill className="text-sm text-primaryBlue ml-1 cursor-pointer" title="The state of IPsec site connection to start in. If disabled (not checked), IPsec site connection does not forward packets." />
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button buttonText="Update" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        name={confirmDelete.name}
        title="Delete Site Connection"
        description="Type the name exactly to confirm deletion"
        onClose={() => setConfirmDelete({ open: false, id: '', name: '' })}
        onConfirm={runDelete}
        isDeleting={loading}
      />
    </div>
  );
};

export default VpnSiteConnections;


